import React, { useState } from 'react';
import moment from 'moment';
import { Chip, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { AccountData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { getTimeFormat } from '../../../../../common/getDateAndTimeFormat';
import { SharedMobileTimePicker } from '../../../../../common/ui/SharedMobileTimePicker';
import { EditOrderFormValues } from '../../EditOrderTypes';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';

const HALF_HOUR_SECONDS = 30 * 60;
const HOUR_SECONDS = 60 * 60;
const EARLIEST_PICKER_HOUR = 6;

export const DurationChips = () => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const [isOtherChosen, setIsOtherChosen] = useState(false);

  const { timeSeconds, privateOrderEndTimeSeconds } = values;
  const duration = privateOrderEndTimeSeconds - timeSeconds;
  const pageDuration: number = editOrderState.orderPage.duration;
  const durations = [pageDuration, pageDuration + HALF_HOUR_SECONDS, pageDuration + HOUR_SECONDS];
  const isOtherSelected = isOtherChosen || !durations.includes(duration);
  const activity = account.activity_label_singular?.[language] ?? t('passes.activity_fallback_singular');

  const hoursLabel = (seconds: number) =>
    t('calendar.create_order.durationHours', { hours: Number((seconds / HOUR_SECONDS).toFixed(2)) });
  const chipLabel = (chipDuration: number) =>
    chipDuration === pageDuration
      ? t('calendar.create_order.activityDuration', { duration: hoursLabel(chipDuration), activity })
      : hoursLabel(chipDuration);

  return (
    <Grid container flexDirection="column" gap={1}>
      <Typography variant="body2" fontWeight={500}>
        {t('calendar.create_order.durationEndsAt', {
          time: moment.unix(privateOrderEndTimeSeconds).tz('utc').format(timeFormat),
        })}
      </Typography>
      <Grid container gap={1}>
        {durations.map((chipDuration) => (
          <Chip
            key={chipDuration}
            label={chipLabel(chipDuration)}
            color={!isOtherSelected && duration === chipDuration ? 'primary' : 'default'}
            variant={!isOtherSelected && duration === chipDuration ? 'filled' : 'outlined'}
            onClick={() => {
              setIsOtherChosen(false);
              void setFieldValue('privateOrderEndTimeSeconds', timeSeconds + chipDuration);
            }}
          />
        ))}
        <Chip
          label={t('calendar.create_order.otherEndTime')}
          color={isOtherSelected ? 'primary' : 'default'}
          variant={isOtherSelected ? 'filled' : 'outlined'}
          onClick={() => setIsOtherChosen(true)}
        />
      </Grid>
      {isOtherSelected && (
        <Grid item maxWidth={120}>
          <SharedMobileTimePicker
            minTime={moment().set({ hour: EARLIEST_PICKER_HOUR, minute: 0, second: 0, millisecond: 0 })}
            label={t('calendar.edit_order.end_time')}
            value={moment.unix(privateOrderEndTimeSeconds).tz('utc')}
            minutesStep={15}
            onChange={(newValue) => {
              void setFieldValue(
                'privateOrderEndTimeSeconds',
                newValue ? newValue.utc().hours() * 3600 + newValue.utc().minutes() * 60 : timeSeconds,
              );
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};
